import React, { createRef, PureComponent } from "react";
import propTypes from "prop-types";
import { toothTypes } from "./Formula/tooth";
import { availableConnectionsTypes } from "./Formula/availableConnections";
import { gapsTypes } from "./Formula/gaps";
import Basic2D from "./Formula2D/Basic2D";
import Formula3DPreview from "./Formula3DPreview";
import { generateFormulaGaps, generateFormulaTooth } from "./Formula/formulaHelpers";
import "./index.scss";
import arrowBottom from "../assets/images/arrowBottom.png";
import { TranslateProvider, useTranslation } from "../i18n";

class FormulaGenerator extends PureComponent {
  formula2DRef = /*#__PURE__*/createRef();
  formula2DHelperRef = /*#__PURE__*/createRef();
  state = {
    formula2DWidth: 0,
    formula2DHeight: 0,
    formula2DHelperHeight: 0,
    initialFormula: {},
    tooth: [],
    gaps: [],
    availableConnections: {},
    prosthesis: {
      activeList: []
    },
    jointDisease: {
      activeList: []
    },
    mandibularCanal: {
      activeList: []
    },
    sinuses: {
      activeList: []
    },
    mucosalDisease: "",
    currentType: "",
    lastAction: "default",
    lastActionItem: "",
    formula3dPreviewFullScreen: false,
    initialSelection: null,
    sectionSelect: false,
    sectionName: null,
    sectionType: null,
    selectableMask: null
  };

  componentDidMount() {
    if (this.formula2DRef) {
      this.setState({
        formula2DWidth: this.formula2DRef.clientWidth,
        formula2DHeight: this.formula2DRef.clientHeight
      });
    }

    if (this.formula2DHelperRef) {
      this.setState({
        formula2DHelperHeight: this.formula2DHelperRef.clientHeight + 10
      });
    }

    if (this.props.projectData && Object.keys(this.props.projectData).length !== 0) {
      this.mountFormulaFromProps();
    } else {
      this.setState({
        tooth: toothTypes.sort((a, b) => a.sort - b.sort),
        gaps: gapsTypes,
        availableConnections: availableConnectionsTypes
      }, () => {
        this.storeInitialFormula();
      });
    }

    this.setState({
      initialSelection: this.props.initialSelection ? this.props.initialSelection : null
    });
  }

  storeInitialFormula() {
    this.setState({
      initialFormula: {
        tooth: this.state.tooth,
        gaps: this.state.gaps,
        availableConnections: this.state.availableConnections,
        prosthesis: this.state.prosthesis,
        jointDisease: this.state.jointDisease,
        mandibularCanal: this.state.mandibularCanal,
        sinuses: this.state.sinuses
      }
    });
  }

  formulaLoadedCallback = () => {
    if (this.state.initialSelection) {
      console.log("Formula loaded callback");
      const selection = this.state.initialSelection;
      this.setState({
        initialSelection: null
      }, () => {
        switch (selection) {
          case "adentySelectTooth":
            this.activateEmptyToothSelect();
            break;

          case "adentySelectDecayedTooth":
            this.activateDecayedToothSelect();
            break;

          case "adentyProstheticsPrepare":
            this.activateProstheticsPrepare();
            break;

          case "implantationSelectTooth":
            this.activateImplantPositionSelect();
            break;

          case "implantationSelectFalseTeeth":
            this.activateImplantFalseTeethSelect();
            break;

          case "implantationSelectFalseTeethConnections":
            this.activateImplantFalseTeethConnectionsSelect();
            break;

          case "removableProstheticsBugelTop":
            this.activateProsthesisSelect("clutchTop", () => {
              this.activateProsthesisToothSelect();
            });
            break;

          case "removableProstheticsBugelBottom":
            this.activateProsthesisSelect("clutchBottom", () => {
              this.activateProsthesisToothSelect();
            });
            break;

          case "removableProstheticsBugelBoth":
            this.activateProsthesisSelect("clutchBoth", () => {
              this.activateProsthesisToothSelect();
            });
            break;

          case "removableProstheticsPlasticTop":
            this.activateProsthesisSelect("acrylicTop", () => {
              this.activateProsthesisToothSelect();
            });
            break;

          case "removableProstheticsPlasticBottom":
            this.activateProsthesisSelect("acrylicBottom", () => {
              this.activateProsthesisToothSelect();
            });
            break;

          case "removableProstheticsPlasticBoth":
            this.activateProsthesisSelect("acrylicBoth", () => {
              this.activateProsthesisToothSelect();
            });
            break;

          case "fixedProstheticsSelectAbutmentTooth":
            this.activateAbutmentToothSelect();
            break;

          case "fixedProstheticsSelectSuspendedTooth":
            this.activateSuspendedToothSelect();
            break;

          case "fixedProstheticsSelectToothConnections":
            this.activateToothConnectionsSelect();
            break;

          case "esteticSelectVeneers":
            this.activateEsteticVeneerToothSelect();
            break;

          case "esteticSelectCrowns":
            this.activateEsteticCrownsToothSelect();
            break;

          case "esteticSelectRestorations":
            this.activateEsteticRestorationToothSelect();
            break;

          case "extraCrownsSelect":
            this.activateExtraCrownsToothSelect();
            break;

          case "extraVeneersSelect":
            this.activateExtraVeneerToothSelect();
            break;

          case "compositeRestorationsSelect":
            this.activateCompositeRestorationsSelect();
            break;

          case "boneAugmentation":
            this.activateBoneAugmentationSelect();
            break;

          case "therapySelectTooth":
            this.activateTherapyPrepare();
            break;

          case "toothDetailing":
            this.activateToothDetailing();
            break;

          case "rootDetailing":
            this.activateRootDetailing();
            break;

          case "restorationSelect":
            this.activateTherapyRestorationSelect();
            break;

          case "restorationDetailing":
            this.activateRestorationDetailing();
            break;

          case "canalFilling":
            this.activateCanalFillingSelect();
            break;

          default:
            console.error("uncatched initial selection");
        }
      });
    }
  };
  mountFormulaFromProps = () => {
    const tooth = generateFormulaTooth(this.props.projectData.tooth);
    const gaps = generateFormulaGaps(this.props.projectData.gaps);
    const prosthesis = {
      activeList: !this.props.projectData.prosthesis ? [] : this.props.projectData.prosthesis
    };
    const jointDisease = {
      activeList: !this.props.projectData.jointDisease ? [] : this.props.projectData.jointDisease
    };
    const mandibularCanal = {
      activeList: !this.props.projectData.mandibularCanal ? [] : this.props.projectData.mandibularCanal
    };
    const sinuses = {
      activeList: !this.props.projectData.sinuses ? [] : this.props.projectData.sinuses
    };
    const mucosalDisease = this.props.projectData.mucosalDisease;
    this.setState({
      tooth,
      gaps,
      prosthesis,
      jointDisease,
      mandibularCanal,
      sinuses,
      mucosalDisease,
      availableConnections: availableConnectionsTypes
    }, () => {
      this.storeInitialFormula();
    });
  }; // Запись активности

  setActivity = itemsSet => {
    const newActivitySet = Object.keys(itemsSet).reduce((prepareSet, itemType) => {
      switch (itemType) {
        case "tooth":
          return { ...prepareSet,
            [itemType]: itemsSet[itemType].map(el => ({
              name: el.name,
              toothElementsActive: el.toothElementsActive,
              toothSegments: el.toothSegments,
              position: el.position
            }))
          };

        case "gaps":
          return { ...prepareSet,
            [itemType]: itemsSet[itemType].map(el => ({
              name: el.name,
              position: el.position,
              connection: el.connection.isActive,
              separation: el.separation.isActive,
              miniImplant: el.miniImplant.isActive
            }))
          };

        case "prosthesis":
        case "jointDisease":
        case "mandibularCanal":
        case "sinuses":
          return { ...prepareSet,
            [itemType]: itemsSet[itemType].activeList
          };

        case "mucosalDisease":
          return { ...prepareSet,
            [itemType]: itemsSet[itemType]
          };

        default:
          console.error("Not supported item type");
          return prepareSet;
      }
    }, {});
    this.props.setProjectDentalData(newActivitySet);
  }; // Туглер кнопки действия

  setActionBtn = elList => (isActive, type) => elList.map(item => ({ ...item,
    actionBtn: { ...item.actionBtn,
      isActive,
      type
    }
  }));
  getAvailableAbutmentTooth = currentTooth => {
    return currentTooth.filter((item, index) => {
      let prev = !!currentTooth[index - 1];
      prev = prev ? currentTooth[index - 1].toothElementsActive.includes("emptyTooth") : false;
      let current = item.toothElementsActive.includes("healthyTooth");
      let next = !!currentTooth[index + 1];
      next = next ? currentTooth[index + 1].toothElementsActive.includes("emptyTooth") : false;
      return prev && current || current && next;
    }).map(item => item.name);
  };
  activateAbutmentToothSelect = () => {
    const currentTooth = this.state.tooth;
    const availableTooth = [...this.getAvailableAbutmentTooth(currentTooth.filter(el => el.position === "top")), ...this.getAvailableAbutmentTooth(currentTooth.filter(el => el.position === "bottom"))];
    const tooth = this.state.tooth.map(item => {
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: availableTooth.includes(item.name),
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "AbutmentToothSelect",
      lastAction: "AbutmentToothSelect",
      lastActionItem: ""
    });
  };
  setAbutmentToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        return { ...item,
          toothElementsActive: item.toothElementsActive.includes("plasticCrown") ? [...item.toothElementsActive.filter(el => el !== "plasticCrown")] : [...item.toothElementsActive, "plasticCrown"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "AbutmentToothSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateSuspendedToothSelect = () => {
    const tooth = this.state.tooth.map(item => {
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: item.toothElementsActive.some(el => ["emptyTooth", "plasticTooth"].includes(el)),
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "SuspendedToothSelect",
      lastAction: "SuspendedToothSelect",
      lastActionItem: ""
    });
  };
  setSuspendedToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        return { ...item,
          toothElementsActive: item.toothElementsActive.includes("plasticTooth") ? ["emptyTooth"] : ["plasticTooth"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "SuspendedToothSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateExtraCrownsToothSelect = () => {
    // "sealedCanal", "toothStumpRestoration", "stumpTabs"
    const tooth = this.state.tooth.map(item => {
      let onlyHealthyTooth = item.toothElementsActive.length === 1 && item.toothElementsActive.includes("healthyTooth");
      let completeImplantation = item.toothElementsActive.includes("plasticCrown") && item.toothElementsActive.includes("implantPosition");
      let isActive = onlyHealthyTooth || item.toothElementsActive.some(el => ["plasticCrown", "sealedCanal", "toothStumpRestoration", "stumpTabs"].includes(el));

      if (isActive) {
        // if (item.isEighth) {
        // isActive = false;
        // }
        if (completeImplantation) {
          isActive = false;
        }
      }

      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive,
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "ExtraCrownToothSelect",
      lastAction: "ExtraCrownToothSelect",
      lastActionItem: ""
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setExtraCrownsToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        let isPresent = item.toothElementsActive.includes("plasticCrown");
        let baseState = this.state.initialFormula.tooth.find(el => el.name === name);
        baseState = { ...baseState,
          toothElementsActive: baseState.toothElementsActive.filter(el => el !== "plasticCrown")
        };
        return { ...item,
          toothElementsActive: isPresent ? baseState.toothElementsActive : [...item.toothElementsActive, "plasticCrown"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "ExtraCrownToothSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateExtraVeneerToothSelect = () => {
    const tooth = this.state.tooth.map(item => {
      let onlyHealthyTooth = item.toothElementsActive.length === 1 && item.toothElementsActive.includes("healthyTooth");
      let isActive = onlyHealthyTooth || item.toothElementsActive.some(el => ["sealedCanal", "veneerTab"].includes(el));

      if (isActive) {
        if (item.isEighth) {
          isActive = false;
        } else if (item.toothElementsActive.includes("plasticCrown")) {
          isActive = false;
        }
      }

      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive,
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "ExtraVeneerToothSelect",
      lastAction: "ExtraVeneerToothSelect",
      lastActionItem: ""
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setExtraVeneerToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        let isPresent = item.toothElementsActive.includes("veneerTab");
        let baseState = this.state.initialFormula.tooth.find(el => el.name === name);
        baseState = { ...baseState,
          toothElementsActive: baseState.toothElementsActive.filter(el => el !== "veneerTab")
        };
        return { ...item,
          toothElementsActive: isPresent ? baseState.toothElementsActive : [...item.toothElementsActive, "veneerTab"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "ExtraVeneerToothSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateEsteticVeneerToothSelect = () => {
    const tooth = this.state.tooth.map(item => {
      return { ...item,
        toothElementsActive: item.toothElementsActive.includes("healthyTooth") ? ["oldEsteticTooth"] : item.toothElementsActive,
        actionBtn: { ...item.actionBtn,
          isActive: !item.isEighth && item.toothElementsActive.some(el => ["healthyTooth", "oldEsteticTooth", "veneerTab"].includes(el)),
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "EsteticVeneerToothSelect",
      lastAction: "EsteticVeneerToothSelect",
      lastActionItem: ""
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setEsteticVeneerToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        return { ...item,
          toothElementsActive: item.toothElementsActive.includes("veneerTab") ? ["oldEsteticTooth"] : ["veneerTab"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "EsteticVeneerToothSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateEsteticCrownsToothSelect = () => {
    const tooth = this.state.tooth.map(item => {
      return { ...item,
        toothElementsActive: item.toothElementsActive.includes("healthyTooth") ? ["oldEsteticTooth"] : item.toothElementsActive,
        actionBtn: { ...item.actionBtn,
          isActive: !item.isEighth && item.toothElementsActive.some(el => ["healthyTooth", "oldEsteticTooth", "plasticCrown"].includes(el)),
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "EsteticCrownToothSelect",
      lastAction: "EsteticCrownToothSelect",
      lastActionItem: ""
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setEsteticCrownsToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        return { ...item,
          toothElementsActive: item.toothElementsActive.includes("plasticCrown") ? ["oldEsteticTooth"] : ["plasticCrown"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "EsteticCrownToothSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateEsteticRestorationToothSelect = () => {
    const tooth = this.state.tooth.map(item => {
      return { ...item,
        toothElementsActive: item.toothElementsActive.includes("healthyTooth") ? ["oldEsteticTooth"] : item.toothElementsActive,
        actionBtn: { ...item.actionBtn,
          isActive: !item.isEighth && item.toothElementsActive.some(el => ["healthyTooth", "oldEsteticTooth", "newEsteticTooth"].includes(el)),
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "EsteticRestorationToothSelect",
      lastAction: "EsteticRestorationToothSelect",
      lastActionItem: ""
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setEsteticRestorationToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        return { ...item,
          toothElementsActive: item.toothElementsActive.includes("newEsteticTooth") ? ["oldEsteticTooth"] : ["oldEsteticTooth", "newEsteticTooth"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "EsteticRestorationToothSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateCompositeRestorationsSelect = () => {
    const tooth = this.state.tooth.map(item => {
      let exists = item.toothElementsActive.some(el => ["healthyTooth", "newEsteticTooth"].includes(el));

      if (exists) {
        exists = !item.toothElementsActive.some(el => ["plasticCrown", "veneerTab", "toothStumpRestoration", "stumpTabs"].includes(el));
      }

      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: !item.isEighth && exists,
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "CompositeRestorationsSelect",
      lastAction: "CompositeRestorationsSelect",
      lastActionItem: ""
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setCompositeRestorationsSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        let isPresent = item.toothElementsActive.includes("newEsteticTooth");
        let baseState = this.state.initialFormula.tooth.find(el => el.name === name);
        baseState = { ...baseState,
          toothElementsActive: [...baseState.toothElementsActive.filter(el => !["oldEsteticTooth", "newEsteticTooth", "healthyTooth"].includes(el)), "healthyTooth"]
        };
        return { ...item,
          toothElementsActive: isPresent ? baseState.toothElementsActive : [...item.toothElementsActive.filter(el => "healthyTooth" !== el), "oldEsteticTooth", "newEsteticTooth"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "CompositeRestorationsSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateToothConnectionsSelect = () => {
    const tooth = this.state.tooth;
    const availableTypes = ["plasticCrown", "plasticTooth"];
    const availableTooth = tooth.filter((item, index) => {
      let prev = !!tooth[index - 1] ? tooth[index - 1].toothElementsActive.some(el => availableTypes.includes(el)) : false;
      let current = item.toothElementsActive.some(el => availableTypes.includes(el));
      let next = !!tooth[index + 1] ? tooth[index + 1].toothElementsActive.some(el => availableTypes.includes(el)) : false;
      return prev && current || current && next;
    }).map(item => item.name);
    const availableConnections = availableTooth.reduce((sum, el) => [...sum, ...this.state.availableConnections[el]], []).filter((item, pos, arr) => arr.indexOf(item) === pos).filter(item => availableTooth.includes(item.match(/.{1,2}/g)[0]) && availableTooth.includes(item.match(/.{1,2}/g)[1]));
    const gaps = this.state.gaps.map(item => ({ ...item,
      actionBtn: { ...item.actionBtn,
        isActive: availableConnections.includes(item.name) && !item.separation.isActive,
        type: item.connection.isActive && availableConnections.includes(item.name) ? "activeBtn" : "nonActiveBtn"
      },
      connection: { ...item.connection,
        isActive: item.connection.isActive && availableConnections.includes(item.name)
      }
    }));
    this.setState({
      tooth,
      gaps,
      currentType: "ConnectionsSelect",
      lastAction: "ConnectionsSelect",
      lastActionItem: ""
    });
    this.setActivity({
      gaps: gaps,
      tooth: tooth
    });
  };
  openProstheticsTypeSelect = (name, isActive) => {
    const tooth = this.state.tooth.map(item => ({ ...item,
      prostheticsTypeSelect: item.name === name ? isActive : false
    }));
    this.setState({
      tooth
    });
  };
  openImplantsTypeSelect = (name, isActive) => {
    const tooth = this.state.tooth.map(item => ({ ...item,
      implantsTypeSelect: item.name === name ? isActive : false
    }));
    this.setState({
      tooth
    });
  };
  setImplantsTypeSelect = (name, type) => {
    const exceptList = ["multiunitAbutment", "multiunitZirconiumAbutment", "singleStageImplant", "standardAbutment", "individualZirconiumAbutment", "individualTitaniumAbutment", "screwFixationImplants", "toothRemoval"];
    const tooth = this.state.tooth.map(item => ({ ...item,
      toothElementsActive: item.name === name ? [...item.toothElementsActive.filter(el => !exceptList.includes(el)), type] : item.toothElementsActive,
      implantsTypeSelect: false
    }));
    this.setState({
      tooth,
      lastAction: type,
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateImplantFalseTeethConnectionsSelect = () => {
    const tooth = this.setActionBtn(this.state.tooth)(false, "actionBtn");
    const cancelPacks = [["healthyTooth"], ["healthyTooth", "sealedCanal"], ["healthyTooth", "stumpTabs"], ["healthyTooth", "toothStumpRestoration"], ["healthyTooth", "veneerTab"], ["healthyTooth", "sealedCanal", "veneerTab"], ["emptyTooth"], ["veneerTab"], ["decayedTooth"], ["newEsteticTooth", "oldEsteticTooth"], ["newEsteticTooth", "oldEsteticTooth", "sealedCanal"]];
    const availableTooth = tooth.filter((item, index) => {
      let prev = !!tooth[index - 1] ? cancelPacks.every(pack => pack.sort().toString() !== tooth[index - 1].toothElementsActive.sort().toString()) : false;
      let current = cancelPacks.every(pack => pack.sort().toString() !== item.toothElementsActive.sort().toString());
      let next = !!tooth[index + 1] ? cancelPacks.every(pack => pack.sort().toString() !== tooth[index + 1].toothElementsActive.sort().toString()) : false;
      return prev && current || current && next;
    }).map(item => item.name);
    const availableConnections = availableTooth.reduce((sum, el) => [...sum, ...this.state.availableConnections[el]], []).filter((item, pos, arr) => arr.indexOf(item) === pos).filter(item => availableTooth.includes(item.match(/.{1,2}/g)[0]) && availableTooth.includes(item.match(/.{1,2}/g)[1]));
    const gaps = this.state.gaps.map(item => ({ ...item,
      actionBtn: { ...item.actionBtn,
        isActive: availableConnections.includes(item.name) && !item.separation.isActive,
        type: item.connection.isActive && availableConnections.includes(item.name) ? "activeBtn" : "nonActiveBtn"
      },
      connection: { ...item.connection,
        isActive: item.connection.isActive && availableConnections.includes(item.name)
      }
    }));
    this.setState({
      tooth,
      gaps,
      currentType: "ConnectionsSelect",
      lastAction: "ConnectionsSelect",
      lastActionItem: ""
    });
  };
  setConnectionsSelect = (name, isActive) => {
    const gaps = this.state.gaps.map(item => {
      if (item.name === name) {
        return { ...item,
          actionBtn: { ...item.actionBtn,
            type: isActive ? "activeBtn" : "nonActiveBtn"
          },
          connection: { ...item.connection,
            isActive
          }
        };
      }

      return item;
    });
    this.setState({
      gaps,
      lastAction: "ConnectionsSelect",
      lastActionItem: isActive ? name : ""
    });
    this.setActivity({
      gaps: gaps
    });
  };
  setSeparationsSelect = (name, isActive) => {
    const gaps = this.state.gaps.map(item => {
      if (item.name === name) {
        return { ...item,
          actionBtn: { ...item.actionBtn,
            type: isActive ? "activeBtn" : "nonActiveBtn"
          },
          separation: { ...item.separation,
            isActive
          }
        };
      }

      return item;
    });
    this.setState({
      gaps,
      lastAction: "SeparationsSelect",
      lastActionItem: isActive ? name : ""
    });
    this.setActivity({
      gaps: gaps
    });
  };
  setStumpTabsSelect = name => {
    const implants = ["multiunitAbutment", "multiunitZirconiumAbutment", "singleStageImplant", "standardAbutment", "individualZirconiumAbutment", "individualTitaniumAbutment", "screwFixationImplants", "stumpTabs"];
    const tooth = this.state.tooth.map(item => {
      const isActive = item.toothElementsActive.includes("stumpTabs"); // Удаление лишних элементов (перетирание существующих свойств)

      const toothElementsActive = item.toothElementsActive.filter(el => !implants.includes(el));

      if (item.name === name) {
        return { ...item,
          toothElementsActive: isActive ? toothElementsActive : [...toothElementsActive, "stumpTabs"],
          actionBtn: { ...item.actionBtn,
            type: !isActive ? "activeBtn" : "nonActiveBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "stumpTabs",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setSyntheticGumSelect = name => {
    const activeList = ["syntheticGum"];
    const tooth = this.state.tooth.map(item => {
      const isActive = item.toothElementsActive.includes("syntheticGum");
      const toothElementsActive = item.toothElementsActive.filter(el => !activeList.includes(el));

      if (item.name === name) {
        return { ...item,
          toothElementsActive: isActive ? toothElementsActive : [...toothElementsActive, "syntheticGum"],
          actionBtn: { ...item.actionBtn,
            type: !isActive ? "activeBtn" : "nonActiveBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "syntheticGum",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setBoneRepairSelect = name => {
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("boneRepair");
      const toothElementsActive = present ? item.toothElementsActive.filter(el => el !== "boneRepair") : [...item.toothElementsActive, "boneRepair"];

      if (item.name === name) {
        return { ...item,
          toothElementsActive,
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "boneRepair",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateSinusesSelect = type => {
    const sinuses = { ...this.state.sinuses
    };
    const rightSide = ["hightSinusesRight", "lowSinusesRight", "liftingSinusesRight"];
    const leftSide = ["hightSinusesLeft", "lowSinusesLeft", "liftingSinusesLeft"];

    switch (type) {
      case "hightSinusesRight":
      case "lowSinusesRight":
      case "liftingSinusesRight":
        sinuses.activeList = [...sinuses.activeList.filter(item => !rightSide.includes(item)), type];
        break;

      case "hightSinusesLeft":
      case "lowSinusesLeft":
      case "liftingSinusesLeft":
        sinuses.activeList = [...sinuses.activeList.filter(item => !leftSide.includes(item)), type];
        break;

      case "hightSinusesBoth":
        sinuses.activeList = ["hightSinusesRight", "hightSinusesLeft"];
        break;

      case "lowSinusesBoth":
        sinuses.activeList = ["lowSinusesRight", "lowSinusesLeft"];
        break;

      case "liftingSinusesBoth":
        sinuses.activeList = ["liftingSinusesRight", "liftingSinusesLeft"];
        break;

      default:
        sinuses.activeList = [];
    }

    this.setState({
      sinuses,
      currentType: "SinusesSelect",
      lastAction: type,
      lastActionItem: ""
    });
    this.setActivity({
      sinuses: sinuses
    });
  };
  activateImplantFalseTeethSelect = () => {
    const acceptList = ["emptyTooth"];
    const tooth = this.state.tooth.map(item => {
      const activity = !!item.toothElementsActive.filter(el => acceptList.includes(el)).length;
      const present = item.toothElementsActive.includes("plasticTooth");
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: activity,
          type: present ? "activeBtn" : "nonActiveBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "ImplantFalseTeethSelect",
      lastAction: "ImplantFalseTeethSelect",
      lastActionItem: ""
    });
  };
  setImplantFalseTeethSelect = name => {
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("plasticTooth");
      const toothElementsActive = present ? ["emptyTooth"] : ["plasticTooth"];

      if (item.name === name) {
        return { ...item,
          toothElementsActive,
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "plasticTooth",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateImplantPositionSelect = () => {
    const acceptList = ["emptyTooth", "implantPosition"];
    const tooth = this.state.tooth.map(item => {
      const activity = !!item.toothElementsActive.filter(el => acceptList.includes(el)).length;
      const present = item.toothElementsActive.includes("implantPosition");
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: activity,
          type: present ? "activeBtn" : "nonActiveBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "ImplantPositionSelect",
      lastAction: "ImplantPositionSelect",
      lastActionItem: ""
    });
  };
  setImplantPositionSelect = name => {
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("implantPosition");
      const toothElementsActive = present ? ["emptyTooth"] : ["implantPosition"]; // aditional check for extened items
      // TODO Refactor (hotfix)

      if (item.toothElementsActive.includes("boneRepair")) {
        toothElementsActive.push("boneRepair");
      }

      if (item.toothElementsActive.includes("toothRemoval")) {
        toothElementsActive.push("toothRemoval");
      }

      if (item.name === name) {
        return { ...item,
          toothElementsActive,
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "implantPosition",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  removeAttachmentsForBraces = (tooth, positionArray) => {
    return tooth.map(item => {
      if (positionArray.includes(item.position)) {
        return { ...item,
          toothElementsActive: item.toothElementsActive.filter(el => !["orthodonticAttachments", "buttonImplant"].includes(el))
        };
      }

      return item;
    });
  };
  activateProsthesisSelect = (type, callBack) => {
    const gaps = this.setActionBtn(this.state.gaps)(false, "actionBtn");
    let tooth = this.setActionBtn(this.state.tooth)(false, "actionBtn");
    let prosthesis = { ...this.state.prosthesis
    };

    switch (type) {
      case "capaTop":
      case "elinerTop":
      case "orthodonticPlateTop":
      case "metalBracesTop":
      case "ceramicBracesTop":
      case "sapphireBracesTop":
      case "plasticBracesTop":
      case "lingualBracesTop":
        tooth = this.removeAttachmentsForBraces(tooth, ["top"]);
        break;

      case "capaBottom":
      case "elinerBottom":
      case "orthodonticPlateBottom":
      case "metalBracesBottom":
      case "ceramicBracesBottom":
      case "sapphireBracesBottom":
      case "plasticBracesBottom":
      case "lingualBracesBottom":
        tooth = this.removeAttachmentsForBraces(tooth, ["bottom"]);
        break;

      case "capaBoth":
      case "elinerBoth":
      case "orthodonticPlateBoth":
      case "metalBracesBoth":
      case "ceramicBracesBoth":
      case "sapphireBracesBoth":
      case "plasticBracesBoth":
      case "lingualBracesBoth":
        tooth = this.removeAttachmentsForBraces(tooth, ["top", "bottom"]);
        break;

      default:
    }

    switch (type) {
      case "clutchTop":
      case "acrylicTop":
      case "nylonTop":
      case "capaTop":
      case "elinerTop":
      case "orthodonticPlateTop":
      case "metalBracesTop":
      case "ceramicBracesTop":
      case "sapphireBracesTop":
      case "plasticBracesTop":
      case "lingualBracesTop":
        prosthesis.activeList = prosthesis.activeList.filter(el => !["clutchTop", "acrylicTop", "nylonTop", "capaTop", "elinerTop", "orthodonticPlateTop", "metalBracesTop", "ceramicBracesTop", "sapphireBracesTop", "plasticBracesTop", "lingualBracesTop"].includes(el));
        prosthesis.activeList = [...prosthesis.activeList, type];
        break;

      case "clutchBottom":
      case "acrylicBottom":
      case "nylonBottom":
      case "capaBottom":
      case "elinerBottom":
      case "orthodonticPlateBottom":
      case "metalBracesBottom":
      case "ceramicBracesBottom":
      case "sapphireBracesBottom":
      case "plasticBracesBottom":
      case "lingualBracesBottom":
        prosthesis.activeList = prosthesis.activeList.filter(el => !["clutchBottom", "acrylicBottom", "nylonBottom", "capaBottom", "elinerBottom", "orthodonticPlateBottom", "metalBracesBottom", "ceramicBracesBottom", "sapphireBracesBottom", "plasticBracesBottom", "lingualBracesBottom"].includes(el));
        prosthesis.activeList = [...prosthesis.activeList, type];
        break;

      case "totalTop":
        prosthesis.activeList = prosthesis.activeList.filter(el => !["clutchTop", "acrylicTop", "nylonTop", "capaTop", "elinerTop", "orthodonticPlateTop", "metalBracesTop", "ceramicBracesTop", "sapphireBracesTop", "plasticBracesTop", "lingualBracesTop"].includes(el));
        prosthesis.activeList = [...prosthesis.activeList, "totalTop"];
        tooth = tooth.map(item => {
          let hasProsthetics = item.toothElementsActive.includes("prostheticsTooth");
          const toothElementsActive = !hasProsthetics && item.position === "top" ? ["prostheticsTooth"] : item.toothElementsActive;
          return { ...item,
            toothElementsActive,
            actionBtn: { ...item.actionBtn,
              type: "nonActiveBtn"
            }
          };
        });
        break;

      case "totalBottom":
        prosthesis.activeList = prosthesis.activeList.filter(el => !["clutchBottom", "acrylicBottom", "nylonBottom", "capaBottom", "elinerBottom", "orthodonticPlateBottom", "metalBracesBottom", "ceramicBracesBottom", "sapphireBracesBottom", "plasticBracesBottom", "lingualBracesBottom"].includes(el));
        prosthesis.activeList = [...prosthesis.activeList, "totalBottom"];
        tooth = tooth.map(item => {
          let hasProsthetics = item.toothElementsActive.includes("prostheticsTooth");
          const toothElementsActive = !hasProsthetics && item.position === "bottom" ? ["prostheticsTooth"] : item.toothElementsActive;
          return { ...item,
            toothElementsActive,
            actionBtn: { ...item.actionBtn,
              type: "nonActiveBtn"
            }
          };
        });
        break;

      case "clutchBoth":
        prosthesis.activeList = ["clutchTop", "clutchBottom"];
        break;

      case "acrylicBoth":
        prosthesis.activeList = ["acrylicTop", "acrylicBottom"];
        break;

      case "nylonBoth":
        prosthesis.activeList = ["nylonTop", "nylonBottom"];
        break;

      case "capaBoth":
        prosthesis.activeList = ["capaTop", "capaBottom"];
        break;

      case "elinerBoth":
        prosthesis.activeList = ["elinerTop", "elinerBottom"];
        break;

      case "orthodonticPlateBoth":
        prosthesis.activeList = ["orthodonticPlateTop", "orthodonticPlateBottom"];
        break;

      case "metalBracesBoth":
        prosthesis.activeList = ["metalBracesTop", "metalBracesBottom"];
        break;

      case "ceramicBracesBoth":
        prosthesis.activeList = ["ceramicBracesTop", "ceramicBracesBottom"];
        break;

      case "sapphireBracesBoth":
        prosthesis.activeList = ["sapphireBracesTop", "sapphireBracesBottom"];
        break;

      case "plasticBracesBoth":
        prosthesis.activeList = ["plasticBracesTop", "plasticBracesBottom"];
        break;

      case "lingualBracesBoth":
        prosthesis.activeList = ["lingualBracesTop", "lingualBracesBottom"];
        break;

      case "totalBoth":
        prosthesis.activeList = ["totalTop", "totalBottom"];
        tooth = tooth.map(item => {
          let hasProsthetics = item.toothElementsActive.includes("prostheticsTooth");
          const toothElementsActive = !hasProsthetics ? ["prostheticsTooth"] : item.toothElementsActive;
          return { ...item,
            toothElementsActive,
            actionBtn: { ...item.actionBtn,
              type: "nonActiveBtn"
            }
          };
        });
        break;

      default:
        prosthesis.activeList = [];
        tooth = tooth.map(item => {
          let toothElementsFiltered = item.toothElementsActive.filter(el => !["prostheticsTooth", "dentalBar"].includes(el));
          const toothElementsActive = toothElementsFiltered.length ? toothElementsFiltered : ["healthyTooth"];
          return { ...item,
            toothElementsActive,
            actionBtn: { ...item.actionBtn,
              type: "nonActiveBtn"
            }
          };
        });
    }

    this.setState({
      prosthesis,
      tooth,
      gaps,
      currentType: "SetProsthesis",
      lastAction: type,
      lastActionItem: ""
    }, callBack);
    this.setActivity({
      prosthesis: prosthesis,
      tooth: tooth,
      gaps: gaps
    });
  };
  activateProsthesisToothSelect = () => {
    const prosthesisTop = ["clutchTop", "acrylicTop", "nylonTop", "totalTop"];
    const prosthesisBottom = ["clutchBottom", "acrylicBottom", "nylonBottom", "totalBottom"];
    const isActiveTopProsthesis = this.state.prosthesis.activeList.some(el => prosthesisTop.includes(el));
    const isActiveBottomProsthesis = this.state.prosthesis.activeList.some(el => prosthesisBottom.includes(el));
    let available = [];

    if (isActiveTopProsthesis) {
      available.push("top");
    }

    if (isActiveBottomProsthesis) {
      available.push("bottom");
    }

    const acceptList = ["emptyTooth", "prostheticsTooth"];
    const tooth = this.state.tooth.map(item => {
      const activity = !!item.toothElementsActive.filter(el => acceptList.includes(el)).length;
      let present = item.toothElementsActive.includes("prostheticsTooth");
      let isActive = !item.isEighth && available.includes(item.position) && activity;
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive,
          type: present ? "activeBtn" : "nonActiveBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "ProsthesisToothSelect",
      lastAction: "ProsthesisToothSelect",
      lastActionItem: ""
    });
  };
  setProstheticsToothSelect = name => {
    // TODO here add dentalBar exeption
    const acceptList = ["dentalBar", "implantPosition", "multiunitAbutment", "multiunitZirconiumAbutment", "singleStageImplant", "standardAbutment", "individualZirconiumAbutment", "individualTitaniumAbutment", "telescopicCrown"];
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("prostheticsTooth");

      if (item.name === name) {
        return { ...item,
          toothElementsActive: present ? ["emptyTooth"] : [...item.toothElementsActive.filter(el => acceptList.includes(el)), "prostheticsTooth"],
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      currentType: "ProsthesisToothSelect",
      lastAction: "prostheticsTooth",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setAttachmentsSelect = name => {
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("attachments");
      const toothElementsActive = present ? item.toothElementsActive.filter(el => el !== "attachments") : [...item.toothElementsActive, "attachments"];

      if (item.name === name) {
        return { ...item,
          toothElementsActive,
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "attachments",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  openOrthodonticAttachmentsSelect = (name, isActive) => {
    const tooth = this.state.tooth.map(item => ({ ...item,
      orthodonticAttachmentsSelect: item.name === name ? isActive : false
    }));
    this.setState({
      tooth
    });
  };
  setTelescopicCrownSelect = name => {
    const replaceActivity = ["healthyTooth", "emptyTooth", "veneerTab", "pinTooth", "overPress", "plasticCrown", "plasticTooth", "zirconiumCrown", "zirconiumTooth", "metalCeramicCrown", "metalCeramicTooth", "ceramicCrown", "ceramicTooth", "metalCrown", "metalTooth", "compositeFilling", "temporaryFilling", "dentalBar"];
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("telescopicCrown");
      let toothElementsActive = present ? item.toothElementsActive.filter(el => el !== "telescopicCrown") : [...item.toothElementsActive.filter(item => !replaceActivity.includes(item)), "telescopicCrown"];

      if (toothElementsActive.length === 0) {
        toothElementsActive = ["healthyTooth"];
      }

      if (item.name === name) {
        return { ...item,
          toothElementsActive,
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "telescopicCrown",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setDentalBarSelect = name => {
    // TODO Create rewrite telescopic crowns
    const replaceActivity = ["healthyTooth", "emptyTooth", "veneerTab", "pinTooth", "overPress", "plasticCrown", "plasticTooth", "zirconiumCrown", "zirconiumTooth", "metalCeramicCrown", "metalCeramicTooth", "ceramicCrown", "ceramicTooth", "metalCrown", "metalTooth", "compositeFilling", "temporaryFilling", "telescopicCrown", "multiunitAbutment", "multiunitZirconiumAbutment", "singleStageImplant", "standardAbutment", "individualZirconiumAbutment", "individualTitaniumAbutment", "screwFixationImplants", "implantPosition"];
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("dentalBar");
      let toothElementsActive = present ? item.toothElementsActive.filter(el => el !== "dentalBar") : [...item.toothElementsActive.filter(el => !replaceActivity.includes(el)), "dentalBar"];

      if (toothElementsActive.length === 0) {
        toothElementsActive = ["healthyTooth"];
      }

      if (toothElementsActive.length === 1 && toothElementsActive.includes("dentalBar")) {
        toothElementsActive.push("emptyTooth");
      }

      if (item.name === name) {
        return { ...item,
          toothElementsActive,
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "dentalBar",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateEmptyToothSelect = () => {
    const tooth = this.state.tooth.map(item => {
      let present = item.toothElementsActive.includes("emptyTooth");
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: true,
          type: present ? "activeBtn" : "nonActiveBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "EmptyToothSelect",
      lastAction: "EmptyToothSelect",
      lastActionItem: ""
    });
  };
  setEmptyToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("emptyTooth");

      if (item.name === name) {
        return { ...item,
          toothElementsActive: present ? ["healthyTooth"] : ["emptyTooth"],
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      currentType: "EmptyToothSelect",
      lastAction: "emptyTooth",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateDecayedToothSelect = () => {
    const tooth = this.state.tooth.map(item => {
      let present = item.toothElementsActive.includes("decayedTooth");
      let isActive = item.toothElementsActive.some(el => ["healthyTooth", "decayedTooth"].includes(el));
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive,
          type: present ? "activeBtn" : "nonActiveBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "DecayedToothSelect",
      lastAction: "DecayedToothSelect",
      lastActionItem: ""
    });
  };
  setDecayedToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("decayedTooth");

      if (item.name === name) {
        return { ...item,
          toothElementsActive: present ? ["healthyTooth"] : ["decayedTooth"],
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      currentType: "DecayedToothSelect",
      lastAction: "decayedTooth",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateProstheticsPrepare = () => {
    const tooth = this.state.tooth.map(item => {
      let present = item.toothElementsActive.includes("decayedTooth");
      let isActive = item.toothElementsActive.some(el => ["healthyTooth", "decayedTooth", "sealedCanal", "toothStumpRestoration", "stumpTabs"].includes(el));
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive,
          type: present ? "activeBtn" : "nonActiveBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "ProstheticsPrepare",
      lastAction: "ProstheticsPrepare",
      lastActionItem: ""
    });
  };
  activateTherapyPrepare = () => {
    const tooth = this.state.tooth.map(item => {
      let present = item.toothElementsActive.includes("decayedTooth");
      let isActive = item.toothElementsActive.some(el => ["healthyTooth", "decayedTooth", "sealedCanal", "toothStumpRestoration", "stumpTabs"].includes(el));
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive,
          type: present ? "activeBtn" : "nonActiveBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "TherapyPrepare",
      lastAction: "TherapyPrepare",
      lastActionItem: ""
    });
  };
  activateTherapyRestorationSelect = () => {
    const tooth = this.state.tooth.map(item => {
      console.log(item);
      let isActive = !item.toothElementsActive.some(el => ["therapyRestoration", "emptyTooth"].includes(el));
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive
        }
      };
    });
    this.setState({
      tooth,
      currentType: "RestorationSelect",
      lastAction: "RestorationSelect",
      lastActionItem: ""
    });
  };
  setTherapyRestorationToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        const present = item.toothElementsActive.includes("therapyRestoration");
        let baseState = this.state.initialFormula.tooth.find(el => el.name === name);
        baseState = { ...baseState,
          toothElementsActive: baseState.toothElementsActive.filter(el => el !== "therapyRestoration")
        };
        return { ...item,
          toothElementsActive: present ? baseState.toothElementsActive : [...item.toothElementsActive, "therapyRestoration"],
          toothSegments: present ? { ...baseState.toothSegments
          } : { ...item.toothSegments,
            therapyRestoration: ["sectionTooth_1", "sectionTooth_2", "sectionTooth_3", "sectionTooth_4", "sectionTooth_5", "sectionTooth_6", "sectionTooth_7"]
          },
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth
    });
    this.setActivity({
      tooth: tooth
    });
  };
  toggleOptionSelect = (name, isActive) => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        return { ...item,
          optionSelect: isActive
        };
      }

      return item;
    });
    this.setState({
      tooth
    });
  };
  setOptionSelect = (type, name) => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        let toothElementsActive = [];
        let toothSegments = { ...item.toothSegments
        };

        switch (type) {
          case "sealedCanal":
            toothElementsActive = item.toothElementsActive.filter(el => ["healthyTooth", "plasticCrown", "zirconiumCrown", "metalCeramicCrown", "ceramicCrown", "metalCrown", "temporaryTooth"].includes(el));

            if (toothElementsActive.length === 0) {
              toothElementsActive.push("healthyTooth");
            }

            break;

          case "toothStumpRestoration":
            toothElementsActive = item.toothElementsActive.filter(el => ["healthyTooth", "plasticCrown", "zirconiumCrown", "metalCeramicCrown", "ceramicCrown", "metalCrown", "temporaryTooth"].includes(el));

            if (toothElementsActive.length === 0) {
              toothElementsActive.push("healthyTooth");
            }

            break;

          case "stumpTabs":
            toothElementsActive = item.toothElementsActive.filter(el => !["multiunitAbutment", "multiunitZirconiumAbutment", "singleStageImplant", "standardAbutment", "individualZirconiumAbutment", "individualTitaniumAbutment", "screwFixationImplants", "stumpTabs", "toothStumpRestoration", "sealedCanal", "decayedTooth"].includes(el));

            if (toothElementsActive.length === 0) {
              toothElementsActive.push("healthyTooth");
            }

            break;

          case "cariousTooth":
            // item.toothElementsActive.includes("cariousTooth");
            toothElementsActive = item.toothElementsActive.filter(el => !["emptyTooth", "cariousTooth", "pulpitis", "badSealedCanal", "brokenToolCanal"].includes(el));

            if (toothElementsActive.length === 0) {
              toothElementsActive.push("healthyTooth");
              delete toothSegments["cariousTooth"];
            }

            toothSegments["cariousTooth"] = ["sectionTooth_1"];
            break;

          case "pulpitis":
            toothElementsActive = item.toothElementsActive.filter(el => !["emptyTooth", "cariousTooth", "pulpitis", "badSealedCanal", "brokenToolCanal"].includes(el));

            if (toothElementsActive.length === 0) {
              toothElementsActive.push("healthyTooth");
              delete toothSegments["cariousTooth"];
            }

            toothSegments["cariousTooth"] = ["sectionTooth_1"];
            break;

          case "badSealedCanal":
            toothElementsActive = item.toothElementsActive.filter(el => !["emptyTooth", "cariousTooth", "pulpitis", "badSealedCanal", "brokenToolCanal"].includes(el));

            if (toothElementsActive.length === 0) {
              toothElementsActive.push("healthyTooth");
              delete toothSegments["cariousTooth"];
            }

            toothSegments["cariousTooth"] = ["sectionTooth_1"];
            break;

          case "brokenToolCanal":
            toothElementsActive = item.toothElementsActive.filter(el => !["emptyTooth", "cariousTooth", "pulpitis", "badSealedCanal", "brokenToolCanal"].includes(el));

            if (toothElementsActive.length === 0) {
              toothElementsActive.push("healthyTooth");
              delete toothSegments["cariousTooth"];
            }

            toothSegments["cariousTooth"] = ["sectionTooth_1"];
            break;

          default:
        }

        toothElementsActive.push(type);
        return { ...item,
          toothElementsActive: toothElementsActive,
          toothSegments: toothSegments,
          optionSelect: false
        };
      }

      return item;
    });
    this.setState({
      tooth
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateToothDetailing = () => {
    const tooth = this.state.tooth.map(item => {
      let isActive = item.toothElementsActive.some(el => ["cariousTooth", "pulpitis", "badSealedCanal", "brokenToolCanal"].includes(el));
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive
        }
      };
    });
    this.setState({
      tooth,
      currentType: "ToothDetailingSelect",
      lastAction: "ToothDetailingSelect",
      lastActionItem: ""
    });
  };
  activateRootDetailing = () => {
    const tooth = this.state.tooth.map(item => {
      let isActive = item.toothElementsActive.some(el => ["pulpitis", "badSealedCanal", "brokenToolCanal"].includes(el));
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive
        }
      };
    });
    this.setState({
      tooth,
      currentType: "RootDetailingSelect",
      lastAction: "RootDetailingSelect",
      lastActionItem: ""
    });
  };
  activateRestorationDetailing = () => {
    const tooth = this.state.tooth.map(item => {
      let isActive = item.toothElementsActive.some(el => ["therapyRestoration"].includes(el));
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive
        }
      };
    });
    this.setState({
      tooth,
      currentType: "RestorationDetailingSelect",
      lastAction: "RestorationDetailingSelect",
      lastActionItem: ""
    });
  };
  setDetailingSelect = (name, type) => {
    let mask = null;

    switch (type) {
      case "cariousTooth":
        mask = /^sectionTooth_[1-7]/g;
        break;

      case "therapyRestoration":
        mask = /^sectionTooth_[1-7]/g;
        break;

      case "cyst":
        mask = /^sectionTooth_0[1-3]/g;
        break;

      default:
        mask = null;
    }

    this.setState({
      sectionSelect: true,
      sectionName: name,
      sectionType: type,
      selectableMask: mask
    });
  };
  sectionAction = data => {
    const re = /^[0-9]{2}_[A-z0-9]+_[A-z0-9]+/g;
    let name = "";
    let part = "";

    if (!re.test(data)) {
      this.setState({
        sectionSelect: false,
        sectionName: null,
        sectionType: null
      });
      return;
    }

    let splitedName = data.split("_");
    name = splitedName[0];
    part = `${splitedName[1]}_${splitedName[2]}`;
    this.setToothSegment(name, this.state.sectionType, part);
  };
  setToothSegment = (name, type, sections) => {
    const tooth = this.state.tooth.map(item => {
      if (item.name !== name) {
        return item;
      }

      let segments = [];
      let segmentType = item.toothSegments[type];

      if (!segmentType) {
        segmentType = [];
      }

      if (segmentType.length && segmentType.includes(sections)) {
        segments = segmentType.filter(el => el !== sections);
      } else {
        segments = [...segmentType, sections];
      }

      return { ...item,
        toothSegments: { ...item.toothSegments,
          [type]: segments
        }
      };
    }); // const gaps = this.setActionBtn(this.state.gaps)(false, "actionBtn");

    let fallBackType = "";

    switch (type) {
      case "compositeFilling":
      case "temporaryFilling":
        fallBackType = "FillingsSelect";
        break;

      case "veneerTab":
        fallBackType = "ProsthesisTypeSelect";
        break;

      case "cariousTooth":
        fallBackType = "ToothDetailingSelect";
        break;

      default:
        fallBackType = "";
    }

    this.setState({
      tooth,
      lastAction: fallBackType,
      lastActionItem: ""
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateCanalFillingSelect = () => {
    const tooth = this.state.tooth.map(item => {
      let isActive = !item.toothElementsActive.some(el => ["treatedCanal", "emptyTooth"].includes(el));
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: isActive
        }
      };
    });
    this.setState({
      tooth,
      currentType: "CanalFillingSelect",
      lastAction: "CanalFillingSelect",
      lastActionItem: ""
    });
  };
  setCanalFillingsSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        const present = item.toothElementsActive.includes("treatedCanal");
        let baseState = this.state.initialFormula.tooth.find(el => el.name === name);
        baseState = { ...baseState,
          toothElementsActive: baseState.toothElementsActive.filter(el => el !== "treatedCanal")
        };
        return { ...item,
          toothElementsActive: present ? baseState.toothElementsActive : [...item.toothElementsActive, "treatedCanal"],
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateAdentiaSelect = type => {
    let tooth = [...this.state.tooth];

    switch (type) {
      case "topAdentia":
        tooth = tooth.map(item => ({ ...item,
          toothElementsActive: item.position === "top" ? ["emptyTooth"] : [...item.toothElementsActive]
        }));
        break;

      case "bottomAdentia":
        tooth = tooth.map(item => ({ ...item,
          toothElementsActive: item.position === "bottom" ? ["emptyTooth"] : [...item.toothElementsActive]
        }));
        break;

      case "bothAdentia":
        tooth = tooth.map(item => ({ ...item,
          toothElementsActive: ["emptyTooth"]
        }));
        break;

      default:
        tooth = tooth.map(item => ({ ...item,
          toothElementsActive: ["healthyTooth"]
        }));
    }

    this.setState({
      tooth,
      currentType: "AdentiaSelect",
      lastAction: type,
      lastActionItem: ""
    });
    this.setActivity({
      tooth: tooth
    });
  };
  openFillingsSelect = (name, isActive) => {
    const tooth = this.state.tooth.map(item => ({ ...item,
      fillingsSelect: item.name === name ? isActive : false
    }));
    this.setState({
      tooth
    });
  };
  openPathologiesSelect = (name, isActive) => {
    const tooth = this.state.tooth.map(item => ({ ...item,
      pathologiesSelect: item.name === name ? isActive : false
    }));
    this.setState({
      tooth
    });
  };
  setMiniImplantsSelect = (name, isActive) => {
    const gaps = this.state.gaps.map(item => {
      if (item.name === name) {
        return { ...item,
          actionBtn: { ...item.actionBtn,
            type: isActive ? "activeBtn" : "nonActiveBtn"
          },
          miniImplant: { ...item.miniImplant,
            isActive
          }
        };
      }

      return item;
    });
    this.setState({
      gaps,
      lastAction: "MiniImplantsSelect",
      lastActionItem: isActive ? name : ""
    });
    this.setActivity({
      gaps: gaps
    });
  };
  setButtonsImplantsSelect = name => {
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("buttonImplant");
      const toothElementsActive = present ? item.toothElementsActive.filter(el => el !== "buttonImplant") : [...item.toothElementsActive.filter(el => el !== "orthodonticAttachments"), "buttonImplant"];

      if (item.name === name) {
        return { ...item,
          toothElementsActive,
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "ButtonsImplantsSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  setTemporaryToothSelect = name => {
    const tooth = this.state.tooth.map(item => {
      const present = item.toothElementsActive.includes("temporaryTooth");

      if (item.name === name) {
        let toothElementsActive = item.toothElementsActive.some(el => ["healthyTooth", "compositeFilling", "temporaryFilling", "sealedCanal", "cariousTooth", "pulpitis"].includes(el)) ? [...item.toothElementsActive] : ["healthyTooth"];

        if (!present) {
          toothElementsActive.push("temporaryTooth");
        }

        return { ...item,
          toothElementsActive,
          actionBtn: { ...item.actionBtn,
            type: present ? "nonActiveBtn" : "activeBtn"
          }
        };
      }

      return item;
    });
    this.setState({
      tooth,
      currentType: "TemporaryToothSelect",
      lastAction: "temporaryTooth",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  activateBoneAugmentationSelect = () => {
    const tooth = this.state.tooth.map(item => {
      return { ...item,
        actionBtn: { ...item.actionBtn,
          isActive: item.toothElementsActive.some(el => ["emptyTooth", "plasticTooth", "implantPosition", "boneAugmentation"].includes(el)),
          type: "actionBtn"
        }
      };
    });
    this.setState({
      tooth,
      currentType: "BoneAugmentationSelect",
      lastAction: "BoneAugmentationSelect",
      lastActionItem: ""
    });
  };
  setBoneAugmentationSelect = name => {
    const tooth = this.state.tooth.map(item => {
      if (item.name === name) {
        let isPresent = item.toothElementsActive.includes("boneAugmentation");
        let baseState = this.state.initialFormula.tooth.find(el => el.name === name);
        baseState = { ...baseState,
          toothElementsActive: baseState.toothElementsActive.filter(el => el !== "boneAugmentation")
        };
        return { ...item,
          toothElementsActive: isPresent ? baseState.toothElementsActive : [...item.toothElementsActive, "boneAugmentation"]
        };
      }

      return item;
    });
    this.setState({
      tooth,
      lastAction: "BoneAugmentationSelect",
      lastActionItem: name
    });
    this.setActivity({
      tooth: tooth
    });
  };
  onToothClick = item => {
    switch (this.state.currentType) {
      case "ProsthesisTypeSelect":
        return this.openProstheticsTypeSelect(item.name, !item.prostheticsTypeSelect);

      case "ImplantsTypeSelect":
        return this.openImplantsTypeSelect(item.name, !item.prostheticsTypeSelect);

      case "StumpTabsSelect":
        return this.setStumpTabsSelect(item.name);

      case "SyntheticGumSelect":
        return this.setSyntheticGumSelect(item.name);

      case "ProsthesisToothSelect":
        return this.setProstheticsToothSelect(item.name);

      case "EmptyToothSelect":
        return this.setEmptyToothSelect(item.name);

      case "DecayedToothSelect":
        return this.setDecayedToothSelect(item.name);

      case "ProstheticsPrepare":
      case "TherapyPrepare":
        return this.toggleOptionSelect(item.name, !item.optionSelect);

      case "BoneRepairSelect":
        return this.setBoneRepairSelect(item.name);

      case "TelescopicCrownSelect":
        return this.setTelescopicCrownSelect(item.name);

      case "DentalBarSelect":
        return this.setDentalBarSelect(item.name);

      case "AttachmentsSelect":
        return this.setAttachmentsSelect(item.name);

      case "OrthodonticAttachmentsSelect":
        return this.openOrthodonticAttachmentsSelect(item.name, !item.prostheticsTypeSelect);

      case "ImplantPositionSelect":
        return this.setImplantPositionSelect(item.name);

      case "FillingsSelect":
        return this.openFillingsSelect(item.name, !item.fillingsSelect);

      case "PathologiesSelect":
        return this.openPathologiesSelect(item.name, !item.pathologiesSelect);

      case "ButtonsImplantsSelect":
        return this.setButtonsImplantsSelect(item.name);

      case "TemporaryToothSelect":
        return this.setTemporaryToothSelect(item.name);

      case "ImplantFalseTeethSelect":
        return this.setImplantFalseTeethSelect(item.name);

      case "AbutmentToothSelect":
        return this.setAbutmentToothSelect(item.name);

      case "SuspendedToothSelect":
        return this.setSuspendedToothSelect(item.name);

      case "EsteticVeneerToothSelect":
        return this.setEsteticVeneerToothSelect(item.name);

      case "EsteticCrownToothSelect":
        return this.setEsteticCrownsToothSelect(item.name);

      case "EsteticRestorationToothSelect":
        return this.setEsteticRestorationToothSelect(item.name);

      case "ExtraCrownToothSelect":
        return this.setExtraCrownsToothSelect(item.name);

      case "ExtraVeneerToothSelect":
        return this.setExtraVeneerToothSelect(item.name);

      case "CompositeRestorationsSelect":
        return this.setCompositeRestorationsSelect(item.name);

      case "BoneAugmentationSelect":
        return this.setBoneAugmentationSelect(item.name);

      case "ToothDetailingSelect":
        return this.setDetailingSelect(item.name, "cariousTooth");

      case "RootDetailingSelect":
        return this.setDetailingSelect(item.name, "cyst");

      case "RestorationSelect":
        return this.setTherapyRestorationToothSelect(item.name);

      case "RestorationDetailingSelect":
        return this.setDetailingSelect(item.name, "therapyRestoration");

      case "CanalFillingSelect":
        return this.setCanalFillingsSelect(item.name);

      default:
        return false;
    }
  };
  onGapsClick = gap => {
    switch (this.state.currentType) {
      case "ConnectionsSelect":
        return this.setConnectionsSelect(gap.name, !gap.connection.isActive);

      case "SeparationsSelect":
        return this.setSeparationsSelect(gap.name, !gap.separation.isActive);

      case "MiniImplantsSelect":
        return this.setMiniImplantsSelect(gap.name, !gap.miniImplant.isActive);

      default:
        return false;
    }
  };

  render() {
    const {
      formulaHelperText
    } = this.props;
    const {
      tooth,
      gaps,
      prosthesis,
      jointDisease,
      mandibularCanal,
      sinuses
    } = this.state;
    const stlPack = {
      tooth: tooth,
      prosthesis: prosthesis.activeList,
      jointDisease: [],
      //jointDisease.activeList,
      gaps: gaps,
      mandibularCanal: mandibularCanal.activeList,
      sinuses: sinuses.activeList
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "formulaGenerator"
    }, /*#__PURE__*/React.createElement("div", {
      className: "formula2dPreviewSection",
      ref: node => {
        this.formula2DRef = node;
      }
    }, formulaHelperText && /*#__PURE__*/React.createElement("div", {
      className: "sectionSelectTitle",
      ref: node => {
        this.formula2DHelperRef = node;
      }
    }, /*#__PURE__*/React.createElement("img", {
      src: arrowBottom,
      alt: ""
    }), /*#__PURE__*/React.createElement("span", null, formulaHelperText)), /*#__PURE__*/React.createElement(Basic2D, {
      formulaInformation: stlPack,
      type: this.props.formulaType,
      onToothClick: this.onToothClick,
      onGapsClick: this.onGapsClick,
      toggleOptionSelect: this.toggleOptionSelect,
      setOptionSelect: this.setOptionSelect,
      stateSelectionType: this.state.currentType,
      sectionName: this.state.sectionName,
      targetWidth: this.state.formula2DWidth,
      targetHeight: this.state.formula2DHeight - this.state.formula2DHelperHeight,
      targetTopMargin: this.state.formula2DHelperHeight
    })), /*#__PURE__*/React.createElement("div", {
      className: "formula3dPreviewSection"
    }, this.state.sectionSelect && /*#__PURE__*/React.createElement(SectionSelectHelper, null), /*#__PURE__*/React.createElement(Formula3DPreview, {
      stlPack: stlPack,
      availableSwitchers: this.props.availableSwitchers,
      onLoaded: this.formulaLoadedCallback,
      hideNotUsedSides: this.props.hideNotUsedSides,
      whatNextBtn: true,
      sectionSelect: this.state.sectionSelect,
      sectionName: this.state.sectionName,
      sectionAction: this.sectionAction,
      selectableMask: this.state.selectableMask,
      hideJawStateSwitcher: this.props.hideJawStateSwitcher,
      jawStateEnabled: this.props.jawStateEnabled
    })));
  }

}

const SectionSelectHelper = () => {
  const {
    _t
  } = useTranslation();
  return /*#__PURE__*/React.createElement("div", {
    className: "sectionSelectTitle"
  }, /*#__PURE__*/React.createElement("img", {
    src: arrowBottom,
    alt: ""
  }), /*#__PURE__*/React.createElement("span", null, _t("Выберите необходимую зону")));
};

FormulaGenerator.defaultProps = {
  hideNotUsedSides: true,
  hideJawStateSwitcher: true,
  jawStateEnabled: false,
  formulaHelperText: ""
};
FormulaGenerator.propTypes = {
  setProjectDentalData: propTypes.func.isRequired,
  hideJawStateSwitcher: propTypes.bool,
  jawStateEnabled: propTypes.bool,
  formulaHelperText: propTypes.string
};

const FormulaGeneratorWrapper = props => {
  return /*#__PURE__*/React.createElement(TranslateProvider, null, /*#__PURE__*/React.createElement(FormulaGenerator, props));
};

export default FormulaGeneratorWrapper;